import {css, StyleSheet} from 'aphrodite';
import $ from 'jquery';
import React, {ChangeEvent} from 'react';
import ReactDOM from 'react-dom';

import {ZINDEX_BUFFER_CTRL_F} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ButtonGroup, {ButtonGroupAddon} from 'ui-library/buttons/ButtonGroup';
import {GRAY_DARK, GRAY_DARKER, GRAY_LIGHT} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Spinner from 'ui-library/Spinner';
import * as keyboard from 'util/Keyboard';
import {ElementKeyboardEvent, ElementMouseEvent} from 'util/Types';

export interface CtrlFViewProps {
  selectedIndex: number;
  numSearchResults: number;
  query: string;
  placeholder?: string;
  onClose: () => void;
  onQueryChange: (query: string) => void;
  onViewNextResult: () => void;
  onViewPreviousResult: () => void;
  isLoading?: boolean;
  isTruncated?: boolean;
}

function handleButtonClickPreventDefault(e: ElementMouseEvent): void {
  // Avoid stealing focus when navigating results
  e.preventDefault();
}

/**
 * A replacement for the browser's built-in Ctrl-F UI, particularly useful if the native implementation lacks
 * sufficient rendering context (e.g. virtualization / svg nodes) or domain context (e.g. overlapping results)
 * over is not fully rendered on the page (virtualized) or has domain-specific interpretations of the text query.
 */
class CtrlFView extends React.Component<CtrlFViewProps> {
  searchInputRef = React.createRef<HTMLInputElement>();

  override componentDidMount(): void {
    this.focusAndSelect();
  }

  _handleQueryChange = (e: ChangeEvent<HTMLInputElement>): void => {
    this.props.onQueryChange(e.target.value);
  };

  _handleKeyDown = (e: ElementKeyboardEvent): void => {
    if (keyboard.isEscapeKey(e)) {
      this.props.onClose();
    }
    if (keyboard.isEnterKey(e)) {
      if (e.shiftKey) {
        this.props.onViewPreviousResult();
      } else {
        this.props.onViewNextResult();
      }
    }
  };

  focusAndSelect = (): void => {
    $(ReactDOM.findDOMNode(this.searchInputRef.current)!).focus().select();
  };

  renderSearchResult(): JSX.Element | string | null {
    if (this.props.isLoading) {
      return <Spinner />;
    }
    if (this.props.query) {
      return (
        <span>{`${this.props.selectedIndex + 1} of ${
          this.props.isTruncated ? 'many' : this.props.numSearchResults
        }`}</span>
      );
    }
    return null;
  }

  override render(): JSX.Element {
    return (
      <div className={css(styles.container)} {...getTestProps({component: 'CtrlFView', element: 'root'})}>
        <ButtonGroup isFullWidth={true}>
          <ButtonGroupAddon>
            <input
              ref={this.searchInputRef}
              className={css(styles.input)}
              autoComplete="off"
              type="search"
              tabIndex={-1}
              value={this.props.query}
              onChange={this._handleQueryChange}
              onKeyDown={this._handleKeyDown}
              placeholder={this.props.placeholder}
              {...getTestProps({component: 'CtrlFView', element: 'searchInput'})}
            />
            <div
              {...getTestProps({component: 'CtrlFView', element: 'searchCount'})}
              className={css(styles.inputOverlay)}
            >
              {this.renderSearchResult()}
            </div>
          </ButtonGroupAddon>
          <Button
            className={css(styles.button)}
            buttonStyle={ButtonStyle.TERTIARY_WHITE}
            size={ButtonSize.SMALL}
            onClick={this.props.onViewPreviousResult}
            onMouseDown={handleButtonClickPreventDefault}
            icon="chevron-up"
            iconAltText="Previous result"
            flexWidth={false}
          />
          <Button
            className={css(styles.button)}
            buttonStyle={ButtonStyle.TERTIARY_WHITE}
            size={ButtonSize.SMALL}
            onClick={this.props.onViewNextResult}
            onMouseDown={handleButtonClickPreventDefault}
            icon="chevron-down"
            iconAltText="Next result"
            flexWidth={false}
          />
        </ButtonGroup>
        <button
          {...getTestProps({component: 'CtrlFView', element: 'closeButton'})}
          className={css(styles.closeButton)}
          onClick={this.props.onClose}
          type="button"
        >
          <span className={css(styles.closeButtonIcon)}>
            <BenchlingIcon altText="Close" icon="xmark-large" />
          </span>
        </button>
      </div>
    );
  }
}

// container is {position: fixed}, establishing a stacking context, so we can use relative z-indexes for its
// children to effect how those children elements stack amongst each other.
const INPUT_Z_INDEX = 1;

const styles = StyleSheet.create({
  container: {
    background: GRAY_LIGHT,
    border: `1px solid ${GRAY_DARK}`,
    borderRadius: '0px 0px 4px 4px',
    borderTop: 'none',
    display: 'flex',
    padding: '3px 5px',
    paddingBottom: 5,
    position: 'fixed',
    right: 50,
    top: 0,
    width: 400,
    zIndex: ZINDEX_BUFFER_CTRL_F,
  },
  inputOverlay: {
    alignItems: 'center',
    color: GRAY_DARK,
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    paddingRight: 10,
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: INPUT_Z_INDEX + 1,
  },
  input: {
    zIndex: INPUT_Z_INDEX, // Ensure blue outline appears above adjacent button
  },
  button: {
    flex: 'none',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    outline: 'none',
    ':hover': {
      color: GRAY_DARKER,
    },
  },
  closeButtonIcon: {
    fontSize: 14,
    lineHeight: '24px',
    verticalAlign: 'middle',
  },
});

export default CtrlFView;
